
'use client';

import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useSettings } from './SettingsContext';

type ThemeInfo = {
  id: string;
  name: string;
  className: string;
};

type Theme = ThemeInfo & {
  logoUrl: string;
};

const themeInfoList: ThemeInfo[] = [
  { id: 'default', name: 'Default', className: 'theme-default' },
  { id: 'forest', name: 'Forest', className: 'theme-forest' },
  { id: 'rose', name: 'Rose', className: 'theme-rose' },
  { id: 'ocean', name: 'Ocean', className: 'theme-ocean' },
  { id: 'sunset', name: 'Sunset', className: 'theme-sunset' },
];

interface ThemeContextType {
  themes: Theme[];
  theme: Theme;
  setTheme: (theme: Theme) => void;
  loading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const { settings, globalLogoUrl, loading: settingsLoading } = useSettings();
  const [themes, setThemes] = useState<Theme[]>([]);
  const [theme, setThemeState] = useState<Theme>({ ...themeInfoList[0], logoUrl: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!settingsLoading && settings) {
      const dynamicThemes = themeInfoList.map(info => ({
        ...info,
        logoUrl: info.id === 'default' 
          ? globalLogoUrl 
          : settings[info.id as keyof typeof settings] || `/logos/logo-${info.id}.svg`
      }));
      setThemes(dynamicThemes);

      const savedThemeId = localStorage.getItem('app-theme');
      const savedTheme = dynamicThemes.find(t => t.id === savedThemeId) || dynamicThemes[0];
      setThemeState(savedTheme);
      setLoading(false);
    }
  }, [settings, settingsLoading, globalLogoUrl]);

  const setTheme = (newTheme: Theme) => {
    localStorage.setItem('app-theme', newTheme.id);
    setThemeState(newTheme);
  };
  
  useEffect(() => {
    document.documentElement.className = 'dark'; // Keep dark mode base
    const themeClasses = themeInfoList.map(t => t.className);
    document.documentElement.classList.remove(...themeClasses);
    if (theme.className !== 'theme-default') {
      document.documentElement.classList.add(theme.className);
    }
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ themes, theme, setTheme, loading }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
